/**
 * GsplatWipeTransition - Vertical wipe effect for gaussian splats
 *
 * Creates a horizontal wipe plane that sweeps from top to bottom:
 * - "reveal" mode: splats appear as the plane sweeps down
 * - "hide" mode: splats disappear as the plane sweeps down
 *
 * Used in pairs during splat swaps: hide on old splat + reveal on new splat.
 *
 * Shader functions use PlayCanvas 2.16+ API:
 * - modifySplatCenter, modifySplatRotationScale, modifySplatColor
 * - gsplatGetSizeFromScale, gsplatMakeSpherical
 */
import * as pc from 'playcanvas';
import { relightingSnippetGLSL, relightingSnippetWGSL } from './gsplat-relighting';
import { mirrorClipSnippetGLSL, mirrorClipSnippetWGSL } from './gsplat-mirror-clip';
import { stepEffectTime, type TransitionClock } from './transition-clock';

const wipeShaderGLSL = /* glsl */ `
uniform float uWipeProgress;  // 0.0 = start, 1.0 = fully wiped
uniform float uWipeTop;       // Y coordinate of scene top
uniform float uWipeBottom;    // Y coordinate of scene bottom
uniform float uBandWidth;     // Transition band width in world units
uniform float uMode;          // 1.0 = reveal (show from top), -1.0 = hide (hide from top)
uniform vec3 uEdgeTint;       // Color tint at the wipe edge
uniform float uTime;          // Animation time for edge effects

// Per-splat shared state
float g_visibility;  // 0 = hidden, 1 = visible

// Hash function for per-splat randomization
float hash(vec3 p) {
    return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453);
}

void initWipe(vec3 center) {
    float wipeY = mix(uWipeTop, uWipeBottom, uWipeProgress);

    // Subtle per-splat noise for organic edge (kept small to avoid dead areas between two splats)
    float noise = (hash(center) - 0.5) * uBandWidth * 0.15;
    float threshold = wipeY + noise;
    float halfBand = uBandWidth * 0.5;

    // Compute signed distance from threshold, accounting for sweep direction.
    // For downward sweep (uWipeTop > uWipeBottom): positive = above threshold (already passed).
    // For upward sweep (uWipeTop < uWipeBottom): flip so positive = below threshold (already passed).
    float sweepDir = sign(uWipeTop - uWipeBottom);
    float dist = (center.y - threshold) * sweepDir;
    float raw = smoothstep(-halfBand, halfBand, dist);

    if (uMode > 0.0) {
        // Reveal: splats in the "already passed" region are visible
        g_visibility = raw;
    } else {
        // Hide: splats in the "not yet reached" region are visible
        g_visibility = 1.0 - raw;
    }
}

void modifySplatCenter(inout vec3 center) {
    initWipe(center);
    // No position modification for clean wipe
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_visibility < 0.01) {
        // Fully hidden
        scale = vec3(0.0);
        return;
    }

    if (g_visibility >= 0.99) {
        // Fully visible - keep original
        return;
    }

    // Transition band: shrink splats toward the edge
    float origSize = gsplatGetSizeFromScale(scale);
    float t = g_visibility;

    if (t < 0.3) {
        // Near-invisible: tiny spherical dots
        float dotSize = min(t * 0.15 * origSize, origSize);
        gsplatMakeSpherical(scale, dotSize);
    } else {
        // Partial visibility: scale down proportionally
        scale *= t;
    }
}

void wipeColorEffect(vec3 center, inout vec4 color) {
    if (g_visibility < 0.01 || g_visibility >= 0.99) return;

    // Tint at the wipe edge - strongest at the middle of the band
    float edgeIntensity = sin(g_visibility * 3.14159);
    color.rgb += uEdgeTint * edgeIntensity * 0.4;
}

void modifySplatColor(vec3 center, inout vec4 color) {
    wipeColorEffect(center, color);
}
`;

const wipeShaderWGSL = /* wgsl */ `
uniform uWipeProgress: f32;
uniform uWipeTop: f32;
uniform uWipeBottom: f32;
uniform uBandWidth: f32;
uniform uMode: f32;
uniform uEdgeTint: vec3f;
uniform uTime: f32;

var<private> g_visibility: f32;

fn hash(p: vec3f) -> f32 {
    return fract(sin(dot(p, vec3f(127.1, 311.7, 74.7))) * 43758.5453);
}

fn initWipe(center: vec3f) {
    let wipeY = mix(uniform.uWipeTop, uniform.uWipeBottom, uniform.uWipeProgress);
    let noise = (hash(center) - 0.5) * uniform.uBandWidth * 0.15;
    let threshold = wipeY + noise;
    let halfBand = uniform.uBandWidth * 0.5;

    // Signed distance from threshold, accounting for sweep direction
    let sweepDir = sign(uniform.uWipeTop - uniform.uWipeBottom);
    let dist = (center.y - threshold) * sweepDir;
    let raw = smoothstep(-halfBand, halfBand, dist);

    if (uniform.uMode > 0.0) {
        g_visibility = raw;
    } else {
        g_visibility = 1.0 - raw;
    }
}

fn modifySplatCenter(center: ptr<function, vec3f>) {
    initWipe(*center);
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
    if (g_visibility < 0.01) {
        *scale = vec3f(0.0);
        return;
    }

    if (g_visibility >= 0.99) {
        return;
    }

    let origSize = gsplatGetSizeFromScale(*scale);
    let t = g_visibility;

    if (t < 0.3) {
        let dotSize = min(t * 0.15 * origSize, origSize);
        gsplatMakeSpherical(scale, dotSize);
    } else {
        *scale *= t;
    }
}

fn wipeColorEffect(center: vec3f, color: ptr<function, vec4f>) {
    if (g_visibility < 0.01 || g_visibility >= 0.99) {
        return;
    }

    let edgeIntensity = sin(g_visibility * 3.14159);
    (*color) = vec4f((*color).rgb + uniform.uEdgeTint * edgeIntensity * 0.4, (*color).a);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    wipeColorEffect(center, color);
}
`;

// Cache for the script class
let _GsplatWipeTransitionClass: typeof pc.ScriptType | null = null;

type ScriptTypeWithPrototype<T extends pc.ScriptType> = typeof pc.ScriptType & {
    prototype: T;
};

export interface GsplatWipeTransitionScript extends pc.ScriptType {
    effectTime: number;
    material: pc.Material | null;
    _effectInitialized: boolean;
    _shadersNeedApplication: boolean;
    _edgeTintArray: [number, number, number];
    mode: number;           // 1.0 = reveal, -1.0 = hide
    duration: number;       // seconds
    bandWidth: number;      // world units
    wipeTop: number;        // Y coordinate
    wipeBottom: number;     // Y coordinate
    edgeTint: pc.Color;     // color at wipe edge
    onComplete: (() => void) | null;
    _completed: boolean;
    /** Time source; while `clock.manual` is set the engine tick does not advance the wipe. */
    clock: TransitionClock | null;
    /** Advance by `dtSec` (the video's frame time while the clock is manual). */
    advance: (dtSec: number) => void;
    /** Jump to the end: the final state is written and onComplete runs. */
    finish: () => void;
    _step: (dtSec: number) => void;
    getShaderGLSL: () => string;
    getShaderWGSL: () => string;
    _applyShaders: () => void;
    _applyShaderToMaterial: (material: pc.Material) => void;
    _removeShaders: () => void;
    _updateUniforms: () => void;
    _setUniform: (name: string, value: number | number[] | Float32Array) => void;
}

/**
 * Gets or creates the GsplatWipeTransition script class.
 * Must be called after PlayCanvas app is initialized.
 */
export function getGsplatWipeTransitionClass(): typeof pc.ScriptType {
    if (_GsplatWipeTransitionClass) {
        return _GsplatWipeTransitionClass;
    }

    const GsplatWipeTransition = pc.createScript('gsplatWipeTransition') as ScriptTypeWithPrototype<GsplatWipeTransitionScript>;

    Object.assign(GsplatWipeTransition.prototype, {
        effectTime: 0,
        material: null as pc.Material | null,
        _effectInitialized: false,
        _shadersNeedApplication: false,
        _edgeTintArray: [0, 0, 0] as [number, number, number],

        // Effect properties
        mode: 1.0,              // 1.0 = reveal, -1.0 = hide
        duration: 1.5,          // seconds
        bandWidth: 3.0,         // world units
        wipeTop: 30.0,          // Y coordinate
        wipeBottom: -30.0,      // Y coordinate
        edgeTint: null as pc.Color | null,
        onComplete: null as (() => void) | null,
        _completed: false,
        clock: null as TransitionClock | null,

        initialize(this: GsplatWipeTransitionScript) {
            this.effectTime = 0;
            this.material = null;
            this._effectInitialized = false;
            this._shadersNeedApplication = false;
            this._completed = false;
            this._edgeTintArray = [0, 0, 0];

            if (!this.edgeTint) this.edgeTint = new pc.Color(0.2, 0.6, 1.0); // Light blue

            this.on('enable', () => {
                this.effectTime = 0;
                this._completed = false;
                if (this._effectInitialized) {
                    this._applyShaders();
                } else {
                    this._shadersNeedApplication = true;
                }
            });

            this.on('disable', () => {
                this._removeShaders();
            });

            if (!this.entity.gsplat) {
                return;
            }

            this._effectInitialized = true;
            if (this.enabled) {
                this._applyShaders();
            }
        },

        update(this: GsplatWipeTransitionScript, dt: number) {
            this._step(this.clock?.manual ? 0 : dt);
        },

        advance(this: GsplatWipeTransitionScript, dtSec: number) {
            if (this.enabled) this._step(Math.max(0, dtSec));
        },

        finish(this: GsplatWipeTransitionScript) {
            if (!this.enabled) return;
            this.effectTime = Math.max(this.effectTime, this.duration);
            this._step(0);
            if (this.enabled && !this._completed) {
                // Never got a material to draw with: nothing showed it, so just end it.
                this._completed = true;
                const callback = this.onComplete;
                this.onComplete = null;
                this.enabled = false;
                if (callback) callback();
            }
        },

        _step(this: GsplatWipeTransitionScript, dt: number) {
            // Try to complete initialization
            if (!this._effectInitialized) {
                if (this.entity.gsplat) {
                    this._effectInitialized = true;
                    if (this.enabled && this._shadersNeedApplication) {
                        this._applyShaders();
                        // Only clear the flag if the material was actually obtained
                        if (this.material) {
                            this._shadersNeedApplication = false;
                        }
                    }
                }
                return;
            }

            if (this._shadersNeedApplication) {
                this._applyShaders();
                // Only clear the flag if the material was actually obtained
                if (this.material) {
                    this._shadersNeedApplication = false;
                }
            }

            // Self-heal (mirrors gsplat-compare-clip): the live gsplat material can be rebuilt by
            // the engine a frame or two AFTER entity.enabled (PlayCanvas 2.20 non-unified per-entity
            // material rebuild — see createViewer comment ~14966), or have its gsplatModifyVS chunk
            // replaced by another effect. Without re-resolving, a reused/re-enabled entity animates a
            // detached material (renders solid, no wipe) — the splat-swap cross-fade asymmetry.
            if (this.enabled) {
                let needsReapply = false;
                const gsplat = this.entity.gsplat;
                if (gsplat) {
                    const isUnified = (gsplat as unknown as { unified?: boolean }).unified === true;
                    const currentMat = isUnified
                        ? ((this.app.scene as unknown as { gsplat?: { material?: pc.Material } }).gsplat?.material ?? null)
                        : gsplat.material;
                    if (currentMat && currentMat !== this.material) {
                        this.material = null;
                        needsReapply = true;
                    } else if (this.material) {
                        const device = this.app.graphicsDevice;
                        const lang = (device as unknown as { isWebGPU?: boolean })?.isWebGPU ? 'wgsl' : 'glsl';
                        const chunk = this.material.getShaderChunks(lang).get('gsplatModifyVS');
                        if (!chunk || !chunk.includes('uWipeProgress')) {
                            needsReapply = true;
                        }
                    }
                }
                if (needsReapply) {
                    this._shadersNeedApplication = true;
                    this._applyShaders();
                    if (this.material) {
                        this._shadersNeedApplication = false;
                    }
                }
            }

            if (!this.material) return;

            this.effectTime = stepEffectTime(this.effectTime, dt, this.duration);

            const progress = Math.min(this.effectTime / this.duration, 1.0);

            if (progress >= 1.0) {
                if (this._completed) return;
                this._completed = true;
                // Write the final state once, so a lingering (relight) script ends fully swept.
                this._updateUniforms();
                this.material.update();
                // If relighting is active, stay alive so the combined shader persists.
                // The wipe math is inert (all splats fully visible/hidden),
                // but relighting still needs the shader chunk.
                const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
                if (relightScript && relightScript.enabled) {
                    // Fire callback once
                    if (this.onComplete) {
                        const callback = this.onComplete;
                        this.onComplete = null;
                        callback();
                    }
                    return;
                }
                // No relighting — disable normally
                const callback = this.onComplete;
                this.enabled = false;
                if (callback) callback();
                return;
            }

            this._updateUniforms();
            this.material.update();
        },

        _updateUniforms(this: GsplatWipeTransitionScript) {
            const progress = Math.min(this.effectTime / this.duration, 1.0);
            this._setUniform('uWipeProgress', progress);
            this._setUniform('uWipeTop', this.wipeTop);
            this._setUniform('uWipeBottom', this.wipeBottom);
            this._setUniform('uBandWidth', this.bandWidth);
            this._setUniform('uMode', this.mode);
            this._setUniform('uTime', this.effectTime);
            this._edgeTintArray[0] = this.edgeTint!.r;
            this._edgeTintArray[1] = this.edgeTint!.g;
            this._edgeTintArray[2] = this.edgeTint!.b;
            this._setUniform('uEdgeTint', this._edgeTintArray);
        },

        getShaderGLSL(): string {
            return wipeShaderGLSL;
        },

        getShaderWGSL(): string {
            return wipeShaderWGSL;
        },

        _applyShaders(this: GsplatWipeTransitionScript) {
            const gsplat = this.entity.gsplat;
            if (!gsplat) return;

            if ((gsplat as any).unified) {
                this.material = (this.app.scene as any).gsplat?.material ?? null;
                if (!this.material) {
                    // Retry next frame via update loop
                    this._shadersNeedApplication = true;
                    return;
                }
                this._applyShaderToMaterial(this.material);
            } else {
                const mat = gsplat.material;
                if (mat) {
                    this.material = mat;
                    this._applyShaderToMaterial(mat);
                } else {
                    // Material not ready yet (entity may have just been re-enabled).
                    // Don't rely on once('load') which won't re-fire for already-loaded assets.
                    // Instead, retry via update loop.
                    this._shadersNeedApplication = true;
                }
            }
        },

        _applyShaderToMaterial(this: GsplatWipeTransitionScript, material: pc.Material) {
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
            let shader = lang === 'wgsl' ? this.getShaderWGSL() : this.getShaderGLSL();

            // Check for sibling relighting script - if present, combine shaders
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                // relightingSnippet already includes mirrorClipSnippet, so no separate prepend needed
                const snippet = lang === 'wgsl' ? relightingSnippetWGSL : relightingSnippetGLSL;
                if (lang === 'wgsl') {
                    shader = snippet + '\n' + shader.replace(
                        /fn modifySplatColor\(center: vec3f, color: ptr<function, vec4f>\) \{\s*wipeColorEffect\(center, color\);\s*\}/,
                        `fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {
    applyRelighting(center, color);
    wipeColorEffect(center, color);
}`
                    );
                } else {
                    shader = snippet + '\n' + shader.replace(
                        /void modifySplatColor\(vec3 center, inout vec4 color\) \{\s*wipeColorEffect\(center, color\);\s*\}/,
                        `void modifySplatColor(vec3 center, inout vec4 color) {
    applyRelighting(center, color);
    wipeColorEffect(center, color);
}`
                    );
                }
                relightScript._shaderManagedExternally = true;
            } else {
                // Standalone: prepend mirror clip snippet so mirrorClipSplat() is defined
                const mirrorSnippet = lang === 'wgsl' ? mirrorClipSnippetWGSL : mirrorClipSnippetGLSL;
                shader = mirrorSnippet + shader;
            }

            material.getShaderChunks(lang).set('gsplatModifyVS', shader);
            material.update();
        },

        _removeShaders(this: GsplatWipeTransitionScript) {
            if (!this.material) return;

            // If relighting is active, hand material back and restore standalone relighting shader
            const relightScript = this.entity.script && (this.entity.script as any).gsplatRelighting;
            if (relightScript && relightScript.enabled) {
                relightScript._shaderManagedExternally = false;
                relightScript.material = this.material;
                const device = this.app.graphicsDevice;
                const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';
                const relightShader = lang === 'wgsl' ? relightScript.getShaderWGSL() : relightScript.getShaderGLSL();
                this.material.getShaderChunks(lang).set('gsplatModifyVS', relightShader);
                this.material.update();
                this.material = null;
                console.log('[WipeTransition] Handed material back to relighting');
                return;
            }

            // No relighting — clean up the shader chunk entirely
            const device = this.app.graphicsDevice;
            const lang = (device as any)?.isWebGPU ? 'wgsl' : 'glsl';

            this.material.getShaderChunks(lang).delete('gsplatModifyVS');
            this.material.update();
            this.material = null;
        },

        _setUniform(this: GsplatWipeTransitionScript, name: string, value: number | number[] | Float32Array) {
            this.material?.setParameter(name, value);
        },

        destroy(this: GsplatWipeTransitionScript) {
            this._removeShaders();
        }
    });

    _GsplatWipeTransitionClass = GsplatWipeTransition;
    return GsplatWipeTransition;
}

// For backwards compatibility
export const GsplatWipeTransition = {
    get class() {
        return getGsplatWipeTransitionClass();
    }
};
